import { zodResolver } from '@hookform/resolvers/zod';
import {
  createUserSchema,
  departmentSchema,
  SystemRole,
  type CreateUserInput,
  type DepartmentInput,
} from '@qualicloud/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  AlertDialog,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  FormError,
  IconAlertTriangle,
  IconKey,
  IconLock,
  IconPlus,
  IconSearch,
  IconUsers,
  Input,
  Pagination,
  PasswordInput,
  Select,
  Table,
  Tabs,
  Textarea,
  TreeView,
  useToast,
  type TableColumn,
  type TreeNode,
} from '@/design-system';
import { departmentsApi, queryKeys, toUserMessage, usersApi } from '@/lib/api';
import type { DepartmentNode, UserSummary } from '@/lib/api/types';
import { formatRelative } from '@/lib/format/datetime';
import { SYSTEM_ROLE_LABEL } from '@/lib/i18n';
import { permissionsOfRole } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

export function UsersPage() {
  const [tab, setTab] = useState('usuarios');

  return (
    <>
      <PageHeader
        title="Usuários e departamentos"
        description="Contas de acesso, papéis e a hierarquia de departamentos usada nas regras de distribuição de documentos."
      />
      <PageBody>
        <Tabs
          ariaLabel="Seções de usuários e departamentos"
          value={tab}
          onValueChange={setTab}
          tabs={[
            { value: 'usuarios', label: 'Usuários', content: <UsersSection /> },
            { value: 'departamentos', label: 'Departamentos', content: <DepartmentsSection /> },
          ]}
        />
      </PageBody>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Usuários
// ─────────────────────────────────────────────────────────────────────────────

const createUserFormSchema = createUserSchema.extend({
  initialPassword: z.string().min(1, 'Informe a senha inicial.').max(512),
});
type CreateUserValues = z.infer<typeof createUserFormSchema>;

function UsersSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [unlocking, setUnlocking] = useState<UserSummary | null>(null);

  const filter = { page, pageSize: 25, search: search || undefined, active: onlyActive || undefined };

  const listQuery = useQuery({
    queryKey: queryKeys.users.list(filter),
    queryFn: () => usersApi.list(filter),
    placeholderData: keepPreviousData,
  });

  const unlock = useMutation({
    mutationFn: (user: UserSummary) =>
      usersApi.unlock(user.id, 'Desbloqueio manual solicitado pelo administrador.'),
    onSuccess: async () => {
      setUnlocking(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      toast({ tone: 'success', title: 'Conta desbloqueada' });
    },
    onError: (error) => {
      setUnlocking(null);
      toast({ tone: 'error', title: 'Falha ao desbloquear', description: toUserMessage(error) });
    },
  });

  const columns = useMemo<Array<TableColumn<UserSummary>>>(
    () => [
      {
        id: 'name',
        header: 'Colaborador',
        cell: (row) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-content">
              {row.firstName} {row.lastName}
            </span>
            <span className="block truncate text-xs text-content-subtle">
              {row.username} · {row.email}
            </span>
          </div>
        ),
      },
      {
        id: 'jobTitle',
        header: 'Cargo',
        cell: (row) => <span className="text-content-muted">{row.jobTitle}</span>,
      },
      {
        id: 'status',
        header: 'Situação',
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            <Badge tone={row.active ? 'success' : 'neutral'} srPrefix="Situação da conta:">
              {row.active ? 'Ativa' : 'Inativa'}
            </Badge>
            {row.lockedUntil && new Date(row.lockedUntil) > new Date() ? (
              <Badge tone="danger" icon={<IconLock size={11} />} srPrefix="Aviso:">
                Bloqueada
              </Badge>
            ) : null}
            {row.mustChangePassword ? (
              <Badge tone="warning" srPrefix="Aviso:">
                Troca de senha pendente
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'lastLogin',
        header: 'Último acesso',
        cell: (row) => (
          <span className="text-content-muted">
            {row.lastLoginAt ? formatRelative(row.lastLoginAt) : 'Nunca acessou'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Ações',
        align: 'right',
        /*
         * "Editar" aparece SEMPRE. Antes esta célula devolvia `null` para todo
         * colaborador que não estivesse bloqueado — ou seja, para quase todos —,
         * e a coluna "Ações" ficava vazia numa tela cujo propósito é justamente
         * administrar contas.
         *
         * O efeito prático era pior do que um botão faltando: NÃO HAVIA COMO
         * vincular alguém a um departamento. A API aceita `departmentIds` no
         * `PATCH /users/:id` desde sempre, com trilha de antes/depois, e nenhuma
         * tela chamava. O cadastro também não pergunta departamento, então todo
         * colaborador criado pelo sistema nascia sem lotação — e regra de acesso
         * por departamento não alcança quem não está em nenhum.
         */
        cell: (row) => (
          <div className="flex justify-end gap-2">
            {row.lockedUntil && new Date(row.lockedUntil) > new Date() ? (
              <Button variant="secondary" size="sm" onClick={() => setUnlocking(row)}>
                Desbloquear
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={() => setEditing(row)}>
              Editar
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <form
          role="search"
          className="min-w-[16rem] flex-1"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
          }}
        >
          <label htmlFor="users-search" className="qc-sr-only">
            Buscar colaborador por nome, usuário ou e-mail
          </label>
          <Input
            id="users-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar colaborador…"
            leadingIcon={<IconSearch size={15} />}
          />
        </form>

        <Checkbox
          checked={onlyActive}
          onCheckedChange={(checked) => {
            setOnlyActive(checked);
            setPage(1);
          }}
          label="Apenas contas ativas"
        />

        <Button
          variant="primary"
          leadingIcon={<IconPlus size={15} />}
          onClick={() => setCreating(true)}
        >
          Novo colaborador
        </Button>
      </div>

      <div>
        <Table<UserSummary>
          caption="Colaboradores da organização, com cargo, situação da conta e último acesso."
          columns={columns}
          rows={listQuery.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={listQuery.isPending}
          density="comfortable"
          empty={
            <EmptyState
              icon={<IconUsers size={22} />}
              title="Nenhum colaborador encontrado"
              description="Ajuste a busca ou cadastre um novo colaborador. Contas inativas ficam ocultas quando o filtro está ligado."
            />
          }
        />

        {listQuery.data && listQuery.data.total > 0 ? (
          <div className="rounded-b-lg border-x border-b border-border bg-surface">
            <Pagination
              page={listQuery.data.page}
              pageSize={listQuery.data.pageSize}
              total={listQuery.data.total}
              itemNoun="colaboradores"
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </div>

      {creating ? <CreateUserDialog onClose={() => setCreating(false)} /> : null}
      {editing ? (
        <EditUserDialog user={editing} onClose={() => setEditing(null)} />
      ) : null}

      {unlocking ? (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open) setUnlocking(null);
          }}
          tone="default"
          title={`Desbloquear a conta de ${unlocking.firstName} ${unlocking.lastName}?`}
          description="A conta poderá entrar imediatamente e o contador de tentativas malsucedidas será zerado. O desbloqueio é registrado na trilha de auditoria com o seu nome."
          confirmLabel="Desbloquear conta"
          loading={unlock.isPending}
          onConfirm={() => unlock.mutate(unlocking)}
        />
      ) : null}
    </div>
  );
}

/**
 * EDIÇÃO DO COLABORADOR — e o vínculo com departamentos.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUE ESTE DIÁLOGO PRECISOU EXISTIR
 * ─────────────────────────────────────────────────────────────────────────────
 * `PATCH /users/:id` aceita `departmentIds` desde sempre, sincroniza os
 * vínculos numa transação e emite a trilha com o antes/depois. NENHUMA tela
 * chamava. O cadastro também não pergunta departamento — manda
 * `departmentIds: []` fixo —, então todo colaborador criado pelo sistema nascia
 * sem lotação e não havia como corrigir isso pela interface.
 *
 * Não é detalhe de conveniência: regra de acesso por DEPARTAMENTO é um dos três
 * alcances da ACL de documentos. Colaborador sem lotação não é alcançado por
 * nenhuma delas — e a regra, do ponto de vista de quem a escreveu, simplesmente
 * não funcionava.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * O QUE NÃO SE EDITA AQUI
 * ─────────────────────────────────────────────────────────────────────────────
 * `username` NÃO aparece: §11.300(a) exige que o identificador seja único e
 * permanente, e o serviço o recusa mesmo se enviado. Papel e permissões têm
 * atos próprios, com justificativa obrigatória — mudar autoridade não pode
 * viajar junto de uma correção de sobrenome.
 */
function EditUserDialog({
  user,
  onClose,
}: {
  readonly user: UserSummary;
  readonly onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  /*
   * A LISTA de colaboradores não traz os departamentos de cada um — só
   * `GET /users/:id` traz. Abrir o diálogo com os campos vazios e preenchê-los
   * depois faria os checkboxes piscarem de desmarcados para marcados; por isso
   * o formulário só é montado quando o detalhe chega.
   */
  const detailQuery = useQuery({
    queryKey: queryKeys.users.detail(user.id),
    queryFn: () => usersApi.detail(user.id),
  });

  const departmentsQuery = useQuery({
    queryKey: queryKeys.departments.all(),
    queryFn: () => departmentsApi.tree(),
  });

  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [email, setEmail] = useState(user.email);
  const [jobTitle, setJobTitle] = useState(user.jobTitle);
  const [active, setActive] = useState(user.active);
  const [departmentIds, setDepartmentIds] = useState<string[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const detalhe = detailQuery.data;
  const lotacaoAtual = useMemo(
    () => (detalhe?.departments ?? []).map((d) => d.id),
    [detalhe],
  );

  useEffect(() => {
    if (departmentIds === null && detalhe) setDepartmentIds(lotacaoAtual);
  }, [detalhe, lotacaoAtual, departmentIds]);

  const opcoes = useMemo(
    () => flattenDepartments(departmentsQuery.data ?? []),
    [departmentsQuery.data],
  );

  const salvar = useMutation({
    mutationFn: () =>
      usersApi.update(user.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        jobTitle: jobTitle.trim(),
        active,
        departmentIds: departmentIds ?? lotacaoAtual,
      }),
    onError: (error) => setErro(toUserMessage(error)),
    onSuccess: async () => {
      /*
       * O DIRETÓRIO também é invalidado, e não só a lista de usuários.
       *
       * Ele alimenta os seletores de responsável e de departamento em SEIS
       * telas do SGQ, com cache de 5 minutos. Sem invalidar, alguém acabaria de
       * mover um colaborador de setor e continuaria vendo a lotação antiga ao
       * abrir uma NC — sem nada na tela explicando por quê.
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.users.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.directory.all() }),
      ]);
      toast({ tone: 'success', title: 'Colaborador atualizado' });
      onClose();
    },
  });

  function alternarDepartamento(id: string) {
    setDepartmentIds((atual) => {
      const base = atual ?? lotacaoAtual;
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });
  }

  const marcados = departmentIds ?? lotacaoAtual;

  return (
    <Dialog
      open
      onOpenChange={(aberto) => {
        if (!aberto) onClose();
      }}
      title={`${user.firstName} ${user.lastName}`}
      description={`Conta ${user.username}. O nome de usuário não é alterável — §11.300(a).`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => salvar.mutate()}
            disabled={salvar.isPending || detailQuery.isPending}
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      {detailQuery.isPending ? (
        <p className="text-sm text-content-subtle">Carregando o cadastro…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {erro ? (
            <div className="sm:col-span-2">
              <FormError>{erro}</FormError>
            </div>
          ) : null}

          <Field label="Nome" required>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Sobrenome" required>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
          <Field label="E-mail" required>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field
            label="Cargo"
            required
            description="Congelado em cada assinatura eletrônica — §11.50(a)."
          >
            <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
          </Field>

          <Field
            label="Departamentos"
            className="sm:col-span-2"
            description="Um colaborador pode estar em mais de um. Regras de acesso por departamento só alcançam quem está lotado em algum."
          >
            {opcoes.length === 0 ? (
              <p className="text-sm text-content-subtle">
                Nenhum departamento cadastrado. Crie um na aba Departamentos.
              </p>
            ) : (
              <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {opcoes.map((departamento) => (
                  <li key={departamento.id}>
                    <Checkbox
                      checked={marcados.includes(departamento.id)}
                      onCheckedChange={() => alternarDepartamento(departamento.id)}
                      label={departamento.label}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Field>

          <div className="sm:col-span-2">
            <Checkbox
              checked={active}
              onCheckedChange={(marcado) => setActive(marcado === true)}
              label="Conta ativa"
              description="Desativar impede o acesso sem apagar nada: registros assinados e trilha continuam intactos."
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}

function CreateUserDialog({ onClose }: { readonly onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<CreateUserValues>({
    resolver: zodResolver(createUserFormSchema),
    defaultValues: {
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      role: SystemRole.MEMBER,
      departmentIds: [],
      active: true,
      initialPassword: '',
    },
  });

  const role = form.watch('role');

  /*
   * Sem este campo, TODO colaborador criado pela tela nascia sem lotação:
   * `departmentIds` ia fixo em `[]`. E colaborador sem departamento não é
   * alcançado por nenhuma regra de acesso por departamento — um dos três
   * alcances da ACL de documentos simplesmente não valia para ele.
   */
  const departmentsQuery = useQuery({
    queryKey: queryKeys.departments.all(),
    queryFn: () => departmentsApi.tree(),
  });
  const opcoesDeDepartamento = useMemo(
    () => flattenDepartments(departmentsQuery.data ?? []),
    [departmentsQuery.data],
  );

  const create = useMutation({
    mutationFn: (values: CreateUserValues) => usersApi.create(values as CreateUserInput & { initialPassword: string }),
    onError: (error) => form.setError('root', { message: toUserMessage(error) }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      toast({
        tone: 'success',
        title: 'Colaborador cadastrado',
        description: `A conta "${result.username}" foi criada e exigirá troca de senha no primeiro acesso.`,
      });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="lg"
      title="Novo colaborador"
      description="A conta é criada com senha inicial temporária e troca obrigatória no primeiro acesso."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={() => void form.handleSubmit((values) => create.mutate(values))()}
          >
            Cadastrar
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => create.mutate(values))();
        }}
        noValidate
      >
        {form.formState.errors.root ? (
          <FormError className="sm:col-span-2">{form.formState.errors.root.message}</FormError>
        ) : null}

        <Field label="Nome" required error={form.formState.errors.firstName?.message}>
          <Input {...form.register('firstName')} autoComplete="given-name" />
        </Field>

        <Field label="Sobrenome" required error={form.formState.errors.lastName?.message}>
          <Input {...form.register('lastName')} autoComplete="family-name" />
        </Field>

        <Field
          label="Usuário"
          required
          error={form.formState.errors.username?.message}
          description="Letras, números, ponto, hífen ou sublinhado. Não poderá ser alterado depois: 21 CFR §11.300(a) exige unicidade permanente."
        >
          <Input {...form.register('username')} autoCapitalize="none" spellCheck={false} />
        </Field>

        <Field label="E-mail" required error={form.formState.errors.email?.message}>
          <Input {...form.register('email')} type="email" autoComplete="email" />
        </Field>

        <Field
          label="Cargo"
          required
          error={form.formState.errors.jobTitle?.message}
          description="Congelado em cada assinatura eletrônica como o cargo do signatário no momento do ato (§11.50(a))."
          className="sm:col-span-2"
        >
          <Input {...form.register('jobTitle')} autoComplete="organization-title" />
        </Field>

        <Controller
          control={form.control}
          name="role"
          render={({ field }) => (
            <Field label="Papel" required className="sm:col-span-2">
              <Select
                value={field.value}
                onValueChange={field.onChange}
                options={Object.values(SystemRole).map((value) => ({
                  value,
                  label: SYSTEM_ROLE_LABEL[value],
                }))}
              />
            </Field>
          )}
        />

        <div className="rounded-md border border-border bg-surface-sunken px-3 py-2.5 sm:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
            Permissões concedidas por este papel
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1">
            {permissionsOfRole(role).map((permission) => (
              <li key={permission}>
                <Badge tone="outline" size="sm">
                  {permission}
                </Badge>
              </li>
            ))}
          </ul>
          {role === SystemRole.PLATFORM_ADMIN ? (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-warning-content">
              <IconAlertTriangle size={13} aria-hidden="true" className="mt-0.5 shrink-0" />
              Este é o papel do FORNECEDOR. Ele não recebe permissões de negócio por padrão e cada
              acesso a uma organização cliente gera evento de auditoria.
            </p>
          ) : null}
        </div>

        <Controller
          control={form.control}
          name="departmentIds"
          render={({ field }) => (
            <Field
              label="Departamentos"
              className="sm:col-span-2"
              description="Um colaborador pode estar em mais de um. Regras de acesso por departamento só alcançam quem está lotado em algum — deixar vazio cria uma conta que nenhuma delas enxerga."
            >
              {opcoesDeDepartamento.length === 0 ? (
                <p className="text-sm text-content-subtle">
                  Nenhum departamento cadastrado. Crie um na aba Departamentos.
                </p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {opcoesDeDepartamento.map((departamento) => (
                    <li key={departamento.id}>
                      <Checkbox
                        checked={field.value.includes(departamento.id)}
                        onCheckedChange={() =>
                          field.onChange(
                            field.value.includes(departamento.id)
                              ? field.value.filter((x: string) => x !== departamento.id)
                              : [...field.value, departamento.id],
                          )
                        }
                        label={departamento.label}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Field>
          )}
        />

        <Field
          label="Senha inicial"
          required
          error={form.formState.errors.initialPassword?.message}
          description="Temporária. O colaborador é obrigado a trocá-la no primeiro acesso, e a nova senha passa por toda a política da organização."
          className="sm:col-span-2"
        >
          <PasswordInput {...form.register('initialPassword')} autoComplete="new-password" />
        </Field>
      </form>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Departamentos
// ─────────────────────────────────────────────────────────────────────────────

function DepartmentsSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const treeQuery = useQuery({
    queryKey: queryKeys.departments.tree(),
    queryFn: () => departmentsApi.tree(),
  });

  const detailQuery = useQuery({
    queryKey: queryKeys.departments.detail(selectedId ?? ''),
    queryFn: () => departmentsApi.detail(selectedId as string),
    enabled: Boolean(selectedId),
  });

  const form = useForm<DepartmentInput>({
    resolver: zodResolver(departmentSchema),
    defaultValues: { name: '', description: '', parentId: null, managerUserIds: [] },
  });

  const create = useMutation({
    mutationFn: (values: DepartmentInput) => departmentsApi.create(values),
    onError: (error) => form.setError('root', { message: toUserMessage(error) }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.departments.all() });
      toast({ tone: 'success', title: 'Departamento criado' });
      setCreating(false);
      setSelectedId(result.id);
      form.reset();
    },
  });

  // `?? []` cria array novo a cada render; sem o memo, `flat` recalculava sempre.
  const tree = useMemo(() => treeQuery.data ?? [], [treeQuery.data]);
  const flat = useMemo(() => flattenDepartments(tree), [tree]);

  return (
    <div className="grid gap-5 lg:grid-cols-[20rem_1fr]">
      <Card padding="sm" className="lg:sticky lg:top-20 lg:self-start">
        <div className="mb-2 flex items-center justify-between gap-2 px-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">
            Hierarquia
          </h3>
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<IconPlus size={14} />}
            onClick={() => {
              setCreating(true);
              setSelectedId(null);
              form.reset();
            }}
          >
            Novo
          </Button>
        </div>

        {tree.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<IconUsers size={20} />}
            title="Nenhum departamento"
            description="Departamentos organizam colaboradores e permitem regras de acesso por grupo."
          />
        ) : (
          <TreeView
            ariaLabel="Departamentos"
            nodes={toDepartmentTreeNodes(tree)}
            selectedId={selectedId}
            onSelect={(node) => {
              setCreating(false);
              setSelectedId(node?.id ?? null);
            }}
          />
        )}
      </Card>

      <div className="min-w-0">
        {creating ? (
          <Card>
            <CardHeader headingLevel={3} title="Novo departamento" />
            <form
              className="mt-4 flex max-w-lg flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void form.handleSubmit((values) => create.mutate(values))();
              }}
              noValidate
            >
              {form.formState.errors.root ? (
                <FormError>{form.formState.errors.root.message}</FormError>
              ) : null}

              <Field label="Nome" required error={form.formState.errors.name?.message}>
                <Input {...form.register('name')} maxLength={255} />
              </Field>

              <Controller
                control={form.control}
                name="parentId"
                render={({ field }) => (
                  <Field label="Departamento pai">
                    <Select
                      value={field.value ?? '__root__'}
                      onValueChange={(value) => field.onChange(value === '__root__' ? null : value)}
                      options={[
                        { value: '__root__', label: 'Sem departamento pai' },
                        ...flat.map((dept) => ({ value: dept.id, label: dept.label })),
                      ]}
                    />
                  </Field>
                )}
              />

              <Field label="Descrição" error={form.formState.errors.description?.message}>
                <Textarea {...form.register('description')} rows={3} maxLength={2000} />
              </Field>

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setCreating(false)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" loading={create.isPending}>
                  Criar departamento
                </Button>
              </div>
            </form>
          </Card>
        ) : detailQuery.data ? (
          <Card>
            <CardHeader
              headingLevel={3}
              title={detailQuery.data.name}
              description={detailQuery.data.description || 'Sem descrição.'}
            />

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <section aria-labelledby="dept-managers">
                <h4
                  id="dept-managers"
                  className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-content"
                >
                  <IconKey size={14} aria-hidden="true" className="text-content-subtle" />
                  Gerentes ({detailQuery.data.managers.length})
                </h4>
                {detailQuery.data.managers.length === 0 ? (
                  <p className="text-sm text-content-subtle">
                    Nenhum gerente designado para este departamento.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {detailQuery.data.managers.map((manager) => (
                      <li key={manager.id} className="text-sm text-content">
                        {manager.firstName} {manager.lastName}{' '}
                        <span className="text-content-subtle">({manager.username})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section aria-labelledby="dept-members">
                <h4
                  id="dept-members"
                  className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-content"
                >
                  <IconUsers size={14} aria-hidden="true" className="text-content-subtle" />
                  Membros ({detailQuery.data.members.length})
                </h4>
                {detailQuery.data.members.length === 0 ? (
                  <p className="text-sm text-content-subtle">
                    Nenhum colaborador vinculado. Regras de acesso por departamento não alcançarão
                    ninguém.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {detailQuery.data.members.map((member) => (
                      <li key={member.id} className="text-sm text-content">
                        {member.firstName} {member.lastName}{' '}
                        <span className="text-content-subtle">({member.username})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </Card>
        ) : (
          <Card>
            <EmptyState
              icon={<IconUsers size={22} />}
              title="Selecione um departamento"
              description="Escolha um departamento na hierarquia ao lado para ver gerentes e membros, ou crie um novo."
            />
          </Card>
        )}
      </div>
    </div>
  );
}

function toDepartmentTreeNodes(nodes: readonly DepartmentNode[]): TreeNode[] {
  return nodes.map((node) => ({
    id: node.id,
    label: node.name,
    disabled: !node.active,
    children: node.children.length > 0 ? toDepartmentTreeNodes(node.children) : undefined,
  }));
}

function flattenDepartments(
  nodes: readonly DepartmentNode[],
  prefix = '',
): Array<{ id: string; label: string }> {
  return nodes.flatMap((node) => {
    const label = prefix ? `${prefix} › ${node.name}` : node.name;
    return [{ id: node.id, label }, ...flattenDepartments(node.children, label)];
  });
}
